import { z } from 'zod';
import { chaveDoDiaEmSaoPaulo } from '@/shared/formatters/date';

/**
 * Ids fechados de cada produto, declarados uma vez: os mesmos do catálogo do core, que recusa id
 * de outro catálogo (`briefDtoSchema`). `agreements` nunca é reutilizado com outro sentido.
 */
export const IDS_DOS_PRODUTOS = {
  negociacao: {
    blocks: ['overview', 'funnel', 'agreements', 'objections', 'handoffs', 'timing', 'actions'],
    focuses: ['closing', 'engagement', 'agreements'],
  },
  notificacao: {
    blocks: [
      'overview',
      'channels',
      'delivery',
      'manifestations',
      'batches',
      'evidence',
      'actions',
    ],
    focuses: ['closing', 'delivery', 'manifestations'],
  },
} as const;
export type ProdutoDoRelatorio = keyof typeof IDS_DOS_PRODUTOS;
/** Todo estado e toda resposta valem para um escritório e um produto; trocar qualquer um descarta. */
export interface ReportsScope {
  workspaceId: string;
  produto: ProdutoDoRelatorio;
}
export type BlocoDo<P extends ProdutoDoRelatorio> = (typeof IDS_DOS_PRODUTOS)[P]['blocks'][number];
export type BlockId = BlocoDo<ProdutoDoRelatorio>;
export type FocusId = (typeof IDS_DOS_PRODUTOS)[ProdutoDoRelatorio]['focuses'][number];

export const MAXIMO_DE_BLOCOS = 7;
const TAMANHO_MAXIMO_DO_PEDIDO = 2000;
export const RELATORIOS_POR_PAGINA = 20;
const MAXIMO_DE_RELATORIOS_NA_DEMONSTRACAO = 50;
const JANELA_MAXIMA_EM_DIAS = 366;
const UM_DIA_MS = 86_400_000;

export const reportStatusSchema = z.enum([
  'queued',
  'running',
  'completed',
  'empty',
  'failed',
  'cancelled',
]);
export type ReportStatus = z.infer<typeof reportStatusSchema>;

const metricSchema = z.object({
  label: z.string().max(80),
  value: z.string().max(40),
  detail: z.string().max(160),
});

function esquemasDoProduto<
  B extends readonly [string, ...string[]],
  F extends readonly [string, ...string[]],
>(ids: { blocks: B; focuses: F }) {
  const block = z.enum(ids.blocks);
  const focus = z.enum(ids.focuses);
  const blocks = z.array(block).min(1).max(MAXIMO_DE_BLOCOS);
  const title = z.string().min(1).max(100);
  const briefGravado = z
    .object({
      title,
      start: z.iso.date(),
      end: z.iso.date(),
      focus,
      blocks,
      request: z.string().max(TAMANHO_MAXIMO_DO_PEDIDO).optional(),
    })
    .refine((value) => value.start <= value.end, 'O início deve ser anterior ao fim.');
  // As mesmas recusas do `briefDtoSchema` do core, para o pedido não sair fadado ao 400. Só no
  // pedido novo: o brief de um relatório já gravado não é revalidado contra o relógio daqui.
  const brief = briefGravado.superRefine((value, ctx) => {
    if (!value.title.trim())
      ctx.addIssue({ code: 'custom', path: ['title'], message: 'Dê um nome ao relatório.' });
    if (value.end > (chaveDoDiaEmSaoPaulo(new Date()) ?? value.end))
      ctx.addIssue({
        code: 'custom',
        path: ['end'],
        message: 'O fim do período não pode passar de hoje.',
      });
    if (Date.parse(value.end) - Date.parse(value.start) > (JANELA_MAXIMA_EM_DIAS - 1) * UM_DIA_MS)
      ctx.addIssue({
        code: 'custom',
        path: ['end'],
        message: `O período pode ter no máximo ${JANELA_MAXIMA_EM_DIAS} dias.`,
      });
  });
  // O core devolve o período do pedido inteiro ou nada (`periodoDaSaida`); só uma ponta deixaria a
  // outra presa no brief anterior.
  const plan = z
    .object({
      supported: z.boolean(),
      summary: z.string().max(1000),
      title,
      focus,
      blocks,
      start: z.iso.date().optional(),
      end: z.iso.date().optional(),
    })
    .refine((value) => (value.start === undefined) === (value.end === undefined), {
      message: 'O período do plano vem com início e fim juntos.',
      path: ['end'],
    });
  const preferences = z.object({ remember: z.boolean(), focus, blocks });
  const reportBlock = z.object({
    id: block,
    title: z.string().max(100),
    summary: z.string().max(600),
    metrics: z.array(metricSchema).max(4).optional(),
    bars: z
      .array(
        z.object({
          label: z.string().max(100),
          value: z.number().nonnegative(),
          total: z.number().positive(),
        }),
      )
      .max(6)
      .optional(),
    rows: z
      .array(z.array(z.string().max(160)).max(5))
      .max(6)
      .optional(),
    columns: z.array(z.string().max(60)).max(5).optional(),
    notes: z.array(z.string().max(300)).max(5).optional(),
  });
  const job = z.object({
    id: z.string(),
    workspaceId: z.string(),
    version: z.number().int().nonnegative(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
    status: reportStatusSchema,
    stage: z.number().int().min(0).max(4),
    brief: briefGravado,
    blocks: z.array(reportBlock).max(MAXIMO_DE_BLOCOS),
    demo: z.boolean(),
    warning: z.string().max(500).optional(),
    notificationEnabled: z.boolean(),
  });
  const paginaDeRelatorios = z.object({
    items: z.array(job).max(RELATORIOS_POR_PAGINA),
    nextCursor: z.string().nullable(),
    hasMore: z.boolean(),
    /** De tudo o que a pessoa tem no produto, não só da página: a demonstração não manda. */
    counts: z
      .object({
        all: z.number().int().nonnegative(),
        ready: z.number().int().nonnegative(),
        building: z.number().int().nonnegative(),
      })
      .optional(),
  });
  const demoSnapshot = z.object({
    jobs: z.array(job).max(MAXIMO_DE_RELATORIOS_NA_DEMONSTRACAO),
    preferences,
  });
  return { brief, plan, preferences, reportBlock, job, paginaDeRelatorios, demoSnapshot };
}

/** O produto discrimina os ids aceitos: cada um valida só o próprio catálogo. */
export const ESQUEMAS_DOS_PRODUTOS = {
  negociacao: esquemasDoProduto(IDS_DOS_PRODUTOS.negociacao),
  notificacao: esquemasDoProduto(IDS_DOS_PRODUTOS.notificacao),
} satisfies Record<ProdutoDoRelatorio, unknown>;
export type EsquemasDoProduto = (typeof ESQUEMAS_DOS_PRODUTOS)[ProdutoDoRelatorio];
/**
 * A tela é uma só para os produtos: os tipos aceitam os ids de qualquer catálogo e quem recusa o
 * id de outro produto é o Zod do produto, em runtime, como no core.
 */
type EsquemasDeQualquerProduto = ReturnType<
  typeof esquemasDoProduto<readonly [BlockId, ...BlockId[]], readonly [FocusId, ...FocusId[]]>
>;

export type ReportBrief = z.infer<EsquemasDeQualquerProduto['brief']>;
export type ReportPlan = z.infer<EsquemasDeQualquerProduto['plan']>;
export type ReportPreferences = z.infer<EsquemasDeQualquerProduto['preferences']>;
export type ReportBlock = z.infer<EsquemasDeQualquerProduto['reportBlock']>;
export type ReportJob = z.infer<EsquemasDeQualquerProduto['job']>;
export type PaginaDeRelatorios = z.infer<EsquemasDeQualquerProduto['paginaDeRelatorios']>;
export type ReportSnapshot = z.infer<EsquemasDeQualquerProduto['demoSnapshot']>;

export interface TemplateDoRelatorio {
  id: FocusId;
  title: string;
  description: string;
  prompt: string;
  blocks: BlockId[];
}
/** O que muda de um produto para outro; o resto da tela é o mesmo núcleo. */
export interface ConfigDoProduto {
  produto: ProdutoDoRelatorio;
  rota: string;
  endpoint: string;
  prefixoDoArquivo: string;
  blocos: readonly BlockId[];
  esquemas: EsquemasDoProduto;
  blockLabels: Partial<Record<BlockId, string>>;
  templates: TemplateDoRelatorio[];
  blocosDaDemonstracao: ReportBlock[];
  defaultPreferences: ReportPreferences;
  stages: string[];
  focoInicial: FocusId;
  blocoDoPrimeiroInsight: BlockId;
  /** UX da demonstração: na API quem decide o escopo é o Plano no servidor. */
  palavrasDoModelo: { padrao: RegExp; foco: FocusId }[];
  textos: {
    origem: { rotulo: string; href: string };
    tituloPadrao: string;
    categoria: string;
    semDados: string;
    comecar: string;
    cabecalhoDoDocumento: string;
    carteira: string;
    cabecalhoDoPdf: string;
    resumoDaDemonstracao: (pedido: string) => string;
    foraDoEscopoDaDemonstracao: string;
  };
}
export type FiltroDaBiblioteca = 'all' | 'ready' | 'building';
/** Na ordem em que o diálogo escolhe qual erro mostrar primeiro. */
export const OPERACOES_DO_RELATORIO = [
  'load',
  'poll',
  'open',
  'create',
  'interpret',
  'cancel',
  'preferences',
  'loadMore',
] as const;
export type ReportOperation = (typeof OPERACOES_DO_RELATORIO)[number];
/** Busca e status filtram no servidor; o cursor é opaco e vale só para a mesma consulta. */
export interface ConsultaDaBiblioteca {
  busca?: string;
  status?: ReportStatus[];
}
export interface ReportsGateway {
  interpret(workspaceId: string, text: string, currentBrief?: ReportBrief): Promise<ReportPlan>;
  list(
    workspaceId: string,
    consulta: ConsultaDaBiblioteca & { cursor?: string },
  ): Promise<PaginaDeRelatorios>;
  readPreferences(workspaceId: string): Promise<ReportPreferences>;
  savePreferences(workspaceId: string, preferences: ReportPreferences): Promise<ReportPreferences>;
  create(workspaceId: string, brief: ReportBrief, idempotencyKey: string): Promise<ReportJob>;
  get(workspaceId: string, id: string): Promise<ReportJob>;
  cancel(workspaceId: string, id: string): Promise<ReportJob>;
}
